import { UnitsEnum } from '@/__generated__/graphql';
import { unitSystem as unitSystemState } from '@/components/Analysis/state';
import { getCloudfrontUrl } from '@/utils/cloudfront';
import { calculateDistance3d } from '@/utils/geometry';
import { localiseMeasurement } from '@/utils/unitSystem';
import { Number3, Point2d } from '@fabric-web/3d-viewer';
import { useCallback, useState } from 'react';
import { useAtomValue } from 'jotai';

export const useBlisterRecommendation = () => {
  const unitSystem = useAtomValue(unitSystemState);
  const [angleLoading, setAngleLoading] = useState(false);
  // const { getPixelData, image, error } = useTextureLookup({ url: selectedSpherical?.url || '' });
  const getDistanceFromPoint = useCallback(
    (sphericalPosition: Number3, centerPoint3d: Number3) => {
      let distance: number | undefined;

      const calculatedDistance = calculateDistance3d(sphericalPosition, centerPoint3d);
      if (calculatedDistance) {
        distance = Number(
          localiseMeasurement({
            unitSystem,
            sourceUnit: UnitsEnum.Meter,
            value: Number(calculatedDistance),
            roundToDecimal: 2,
            showUnitLabel: false,
          })
        );
      }
      return { convertedDistance: distance, meterDistance: calculatedDistance };
    },
    [unitSystem]
  );

  // This function is a replica of the useTextureLookup hook from abyss-3d-viewer, as that hook could not be used in a loop due to its input parameters.
  const calculateAngle = useCallback(
    async (angleResourcePath: string, blisterCenterPoint: Point2d) => {
      setAngleLoading(true);
      const response = await fetch(getCloudfrontUrl(angleResourcePath), { credentials: 'include' });
      if (!response.ok) return;
      const blob = await response.blob();

      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const image = new Image();
        image.src = URL.createObjectURL(blob);
        image.addEventListener('load', () => resolve(image));
        image.addEventListener('error', reject);
      });

      const { width, height } = img;

      const canvas = new OffscreenCanvas(width, height);
      const context = canvas.getContext('2d');
      if (!context) return;

      context.drawImage(img, 0, 0, width, height);

      const imageData = context.getImageData(0, 0, width, height, { colorSpace: 'srgb' });

      const xCoordinate = Math.round(blisterCenterPoint.x * width);
      const yCoordinate = Math.round(blisterCenterPoint.y * height);

      const pixelIndex = yCoordinate * imageData.width + xCoordinate;
      const pixelData = imageData.data.slice(pixelIndex * 4, 4 * (pixelIndex + 1));
      setAngleLoading(false);
      return pixelData[0];
    },
    []
  );

  return {
    getDistanceFromPoint,
    calculateAngle,
    angleLoading,
  };
};
